import React from 'react';
import { IconButton, Tooltip, Whisper } from 'rsuite';
import MicIcon from '@rsuite/icons/legacy/Microphone';
import MicOffIcon from '@rsuite/icons/legacy/MicrophoneSlash';
import { RecognitionStatus, SpeechRecognitionError } from '../../services/speechRecognition';

const COLOR_DISABLED = '#666';
const COLOR_WARNING = '#E6A23C';
const COLOR_LISTENING = '#D06662';
const COLOR_IDLE = '#3B888C';

export interface SubtitleControlsProps {
  /** Whether speech recognition is listening */
  isListening: boolean;
  /** Current recognition status */
  status: RecognitionStatus;
  /** Whether speech recognition is supported */
  isSupported: boolean;
  /** Toggle function */
  onToggle: () => void;
  /** Current error if any */
  error?: SpeechRecognitionError | null;
  /** Button size */
  size?: 'lg' | 'md' | 'sm' | 'xs';
}

/**
 * Control button for interpreter's speech-to-text subtitles.
 * Shows a microphone toggle with visual feedback.
 */
const SubtitleControls: React.FC<SubtitleControlsProps> = ({
  isListening,
  status,
  isSupported,
  onToggle,
  error,
  size = 'lg',
}) => {
  if (!isSupported) {
    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>Speech-to-text not supported in this browser</Tooltip>}
      >
        <IconButton
          icon={<MicOffIcon />}
          size={size}
          disabled
          style={{ backgroundColor: COLOR_DISABLED, opacity: 0.5 }}
          appearance="primary"
        />
      </Whisper>
    );
  }

  const isProcessing =
    status === RecognitionStatus.STARTING || status === RecognitionStatus.STOPPING;

  // Resolve the visual state once: errors outrank listening outranks idle.
  // The button always stays clickable on error so the user can retry —
  // start() clears the error (e.g. after granting mic permission).
  let tooltipText: string;
  let backgroundColor: string;
  if (error && !error.recoverable) {
    tooltipText = `${error.message} (Click to retry)`;
    backgroundColor = COLOR_DISABLED;
  } else if (error) {
    tooltipText = `${error.message} (Click to retry)`;
    backgroundColor = COLOR_WARNING;
  } else if (isListening) {
    tooltipText = 'Click to stop subtitles';
    backgroundColor = COLOR_LISTENING;
  } else {
    tooltipText = 'Click to start subtitles for the user';
    backgroundColor = COLOR_IDLE;
  }

  return (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={<Tooltip>{tooltipText}</Tooltip>}
    >
      <IconButton
        icon={isListening ? <MicIcon /> : <MicOffIcon />}
        size={size}
        onClick={onToggle}
        disabled={isProcessing}
        style={{ backgroundColor, marginLeft: 5 }}
        appearance="primary"
      />
    </Whisper>
  );
};

export default SubtitleControls;
